import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useGeoTracking } from './hooks/useGeoTracking';
import { useOfflineQueue } from './hooks/useOfflineQueue';
import Login from './pages/Login';
import Home from './pages/Home';
import FormMantenimiento from './pages/FormMantenimiento';
import FormVisita from './pages/FormVisita';
import Historial from './pages/Historial';
import Admin from './pages/Admin';
import Bitacoras from './pages/Bitacoras';
import NotificacionesPWA from './pages/NotificacionesPWA';
import Rendiciones from './pages/Rendiciones';
import Pendientes from './pages/Pendientes';
import Planificacion from './pages/Planificacion';
import FormDetalle from './pages/FormDetalle';
import ResetPassword from './pages/ResetPassword';
import Directorio from './pages/Directorio';
import SolicitarInsumos from './pages/SolicitarInsumos';
import api from './utils/api';
import './index.css';

// Inicializar Firebase solo si las variables de entorno están configuradas
let messaging = null;
let getToken  = null;
let onMessage = null;
if (process.env.REACT_APP_FIREBASE_API_KEY) {
  try {
    const fb = require('./firebase');
    messaging = fb.messaging;
    getToken  = fb.getToken;
    onMessage = fb.onMessage;
  } catch (e) {
    console.warn('Firebase no disponible:', e.message);
  }
}

function GeoTracker() {
  const { tecnico } = useAuth();
  useGeoTracking(tecnico?.rol === 'tecnico');
  return null;
}

// Bloquea el cierre del navegador mientras haya formularios pendientes de enviar
function QueueGuard() {
  const { cola } = useOfflineQueue();
  useEffect(() => {
    const hayPendientes = cola.some(i => i.estado === 'pendiente' || i.estado === 'enviando');
    if (!hayPendientes) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [cola]);
  return null;
}

function FirebasePushRegistrar() {
  const { tecnico } = useAuth();

  useEffect(() => {
    if (!tecnico || !messaging || !getToken || !onMessage) return;
    if (!('Notification' in window)) return;

    const registrar = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        const fcmToken = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        });

        if (fcmToken) {
          await api.post('/push/token', { token: fcmToken, plataforma: 'web' });
        }
      } catch (err) {
        console.error('Error registrando token push web:', err.message);
      }
    };

    registrar();

    // Notificaciones cuando la PWA está en primer plano
    const unsubscribe = onMessage(messaging, (payload) => {
      const title = payload.notification?.title || 'Solvert';
      const body  = payload.notification?.body  || '';
      if (Notification.permission === 'granted') {
        new Notification(title, { body, icon: '/logo_solvert.jpg' });
      }
    });

    return () => unsubscribe();
  }, [tecnico]);

  return null;
}

function PrivateRoute({ children }) {
  const { tecnico, loading } = useAuth();
  if (loading) return <div className="spinner" style={{ marginTop: 80 }} />;
  return tecnico ? children : <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <GeoTracker />
        <QueueGuard />
        <FirebasePushRegistrar />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<PrivateRoute><Home /></PrivateRoute>} />
          <Route path="/formulario/mantenimiento" element={<PrivateRoute><FormMantenimiento /></PrivateRoute>} />
          <Route path="/formulario/visita" element={<PrivateRoute><FormVisita /></PrivateRoute>} />
          <Route path="/historial" element={<PrivateRoute><Historial /></PrivateRoute>} />
          <Route path="/admin" element={<PrivateRoute><Admin /></PrivateRoute>} />
          <Route path="/bitacoras" element={<PrivateRoute><Bitacoras /></PrivateRoute>} />
          <Route path="/notificaciones" element={<PrivateRoute><NotificacionesPWA /></PrivateRoute>} />
          <Route path="/rendiciones" element={<PrivateRoute><Rendiciones /></PrivateRoute>} />
          <Route path="/pendientes" element={<PrivateRoute><Pendientes /></PrivateRoute>} />
          <Route path="/planificacion" element={<PrivateRoute><Planificacion /></PrivateRoute>} />
          <Route path="/formularios/:id" element={<PrivateRoute><FormDetalle /></PrivateRoute>} />
          <Route path="/directorio" element={<PrivateRoute><Directorio /></PrivateRoute>} />
          <Route path="/insumos" element={<PrivateRoute><SolicitarInsumos /></PrivateRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
